import React, {
  useMemo,
  createContext,
  useEffect,
  useContext,
  useCallback,
} from "react";
import posthog from "posthog-js";
import { safeLocalStorageSetItem } from "utils/localStorage";
import { useHistory } from "react-router-dom";
import {
  useGetContext,
  UserContextResponse,
} from "server/kas/hooks/useGetContext";
import { useGetMe } from "server/kas/hooks/useGetMe";
import { useGetStarfleetToken } from "server/kas/hooks/useGetStarfleetToken";
import {
  KAS_TOKEN_STORAGE_KEY,
  KAS_HOST,
  prependKasUrl,
  redirectUserToLogin,
} from "server/kas/utils";
import { resetPostHogIdentity } from "utils/posthogFeatureFlags";

type KasContextType = {
  isLoading: boolean;
  user: UserContextResponse | undefined;
  isUserLoggedIn: boolean | undefined;
  logout: () => void;
  currentOrgNgcName: string | undefined;
  starfleetIdToken: string | undefined;
  starfleetId: string | undefined;
  starfleetEmail: string | undefined;
};

const defaultValues: KasContextType = {
  isLoading: false,
  user: undefined,
  isUserLoggedIn: false,
  logout: () => {},
  currentOrgNgcName: "",
  starfleetIdToken: "",
  starfleetId: undefined,
  starfleetEmail: undefined,
};

export const KasContext = createContext<KasContextType | undefined>(undefined);

const KasContextProvider = ({ children }: React.PropsWithChildren) => {
  const history = useHistory();
  const { data: userData, error } = useGetContext();
  const { data: userMeData } = useGetMe();
  const { data: starfleetTokenData } = useGetStarfleetToken(history);

  useEffect(() => {
    const handleRdpCheck = async () => {
      if (!starfleetTokenData?.token) return;

      try {
        // Step 1: Check if user is RDP member
        const isRdpResponse = await fetch(
          prependKasUrl("/v2/users/me/developer-programs"),
          {
            headers: { host: KAS_HOST },
            credentials: "include",
            method: "GET",
          }
        );
        const isRdpData = await isRdpResponse.json();

        if (isRdpData?.isRdpMember) {
          return;
        }

        // Step 2: If not RDP member, get NGC user profile
        const ngcProfileResponse = await fetch(
          prependKasUrl("/v2/user-profile"),
          {
            headers: { host: KAS_HOST },
            credentials: "include",
            method: "GET",
          }
        );
        const ngcUserProfileData = await ngcProfileResponse.json();

        if (ngcUserProfileData?.userProfile) {
          const rdpRegistrationResponse = await fetch(
            prependKasUrl("/v2/user-profile"),
            {
              headers: {
                host: KAS_HOST,
                "Content-Type": "application/json",
              },
              credentials: "include",
              method: "PATCH",
              body: JSON.stringify({
                userProfile: {
                  starfleetId: userMeData?.user?.starfleetId,
                  email: ngcUserProfileData?.userProfile?.email,
                  firstName: ngcUserProfileData?.userProfile?.firstName,
                  lastName: ngcUserProfileData?.userProfile?.lastName,
                  country: "United States", // TODO - should prefill with country from voltron
                  locale: "en_us",
                  orgInfo: [
                    {
                      jobTitle:
                        ngcUserProfileData.userProfile.orgInfo?.[0]?.jobTitle ??
                        "engineer",
                      organization: "", // TODO - can remove once BE makes this optional
                    },
                  ],
                  preferences: ["generative_ai"],
                  rdpSource: "brev",
                  rdpLogin: true,
                  rdpLoginDate: new Date().toISOString(),
                  devEmailable:
                    ngcUserProfileData.userProfile.devEmailable ?? false,
                  entEmailable:
                    ngcUserProfileData.userProfile.entEmailable ?? false,
                },
              }),
            }
          );

          if (rdpRegistrationResponse.ok) {
            console.log("User successfully registered for RDP");
          } else {
            console.error(
              "Failed to register user for RDP:",
              await rdpRegistrationResponse.text()
            );
          }
        }
      } catch (error) {
        console.error("Error in sequential API calls:", error);
      }
    };

    handleRdpCheck();
  }, [starfleetTokenData?.token, userMeData?.user]);

  // ensure the user's logged in state
  // is updated based off the heartbeat
  useEffect(() => {
    if (!userData && error) {
      localStorage.removeItem(KAS_TOKEN_STORAGE_KEY);
      history.push("/login");
    }
  }, [error, history, userData]);

  // set the starfleet token in state + local storage
  useEffect(() => {
    if (starfleetTokenData?.token) {
      safeLocalStorageSetItem(KAS_TOKEN_STORAGE_KEY, starfleetTokenData?.token);
    }
  }, [starfleetTokenData?.token]);

  const logout = useCallback(() => {
    resetPostHogIdentity(posthog);
    localStorage.removeItem(KAS_TOKEN_STORAGE_KEY);
    redirectUserToLogin();
  }, []);

  const values = useMemo(() => {
    const isLoading =
      starfleetTokenData === undefined || userData === undefined;
    // First-time invitation users have a valid KAS token before they belong to
    // an org, so user creation must not depend on userData.orgName being set.
    const isUserLoggedIn = isLoading
      ? undefined
      : Boolean(starfleetTokenData?.token);
    const orgName = userData?.orgName;
    const defaultOrgName = userMeData?.user?.roles?.[0]?.org?.name;
    return {
      isLoading,
      user: userData,
      isUserLoggedIn,
      logout,
      currentOrgNgcName: orgName ?? defaultOrgName,
      starfleetIdToken: starfleetTokenData?.token,
      starfleetId: userMeData?.user?.starfleetId,
      starfleetEmail: userMeData?.user?.email,
    };
  }, [userData, logout, userMeData, starfleetTokenData]);

  return <KasContext.Provider value={values}>{children}</KasContext.Provider>;
};

// custom hook to read values from
export const useKas = () => {
  const context = useContext(KasContext);
  // throw an error if the user tries to make use of this hook
  // in a component that is not wrapped in the provider
  if (context === undefined) {
    throw new Error(
      "The `useKasContext` hook must be used within a `KasContextProvider` component"
    );
  }
  return context || defaultValues;
};

export default KasContextProvider;
